/**
 * useEditorHistory — Undo/redo hook for canvas editors.
 *
 * Maintains a bounded ring buffer of doc snapshots in refs (so reading
 * the depth doesn't re-render). A single `version` state is used to
 * trigger re-renders only when `canUndo` / `canRedo` actually change.
 *
 * Usage:
 *   const { setDocWithHistory, undo, redo, canUndo, canRedo }
 *     = useEditorHistory(doc, setDoc);
 */
import { useCallback, useEffect, useRef, useState } from "react";

const MAX_HISTORY = 50;

export function useEditorHistory(doc, setDoc) {
   const past = useRef([]);
   const future = useRef([]);
   const [version, setVersion] = useState(0);

   // Reset history when the sheet identity changes
   const sheetKeyRef = useRef(null);
   useEffect(() => {
      if (!doc) return;
      const key = `${doc.project_id || ""}|${doc.sheet_id || ""}`;
      if (sheetKeyRef.current !== key) {
         past.current = [];
         future.current = [];
         sheetKeyRef.current = key;
         setVersion((v) => v + 1);
      }
   }, [doc]);

   const setDocWithHistory = useCallback((updater) => {
      let snapshot = null;
      setDoc((cur) => {
         if (!cur) return cur;
         const next = typeof updater === "function" ? updater(cur) : updater;
         if (next === cur) return cur;
         snapshot = cur;       // capture for post-setState bookkeeping
         return next;
      });
      if (snapshot) {
         past.current.push(snapshot);
         if (past.current.length > MAX_HISTORY) past.current.shift();
         future.current = [];
         setVersion((v) => v + 1);
      }
   }, [setDoc]);

   const undo = useCallback(() => {
      if (past.current.length === 0) return;
      const prev = past.current.pop();
      let captured = null;
      setDoc((cur) => { captured = cur; return prev; });
      if (captured) {
         future.current.push(captured);
         if (future.current.length > MAX_HISTORY) future.current.shift();
      }
      setVersion((v) => v + 1);
   }, [setDoc]);

   const redo = useCallback(() => {
      if (future.current.length === 0) return;
      const next = future.current.pop();
      let captured = null;
      setDoc((cur) => { captured = cur; return next; });
      if (captured) {
         past.current.push(captured);
         if (past.current.length > MAX_HISTORY) past.current.shift();
      }
      setVersion((v) => v + 1);
   }, [setDoc]);

   useEffect(() => {
      const onKey = (e) => {
         const tag = (document.activeElement?.tagName || "").toLowerCase();
         if (tag === "input" || tag === "textarea") return;
         const meta = e.metaKey || e.ctrlKey;
         if (!meta) return;
         if (e.key === "z" || e.key === "Z") {
            e.preventDefault();
            if (e.shiftKey) redo();
            else undo();
         } else if (e.key === "y" || e.key === "Y") {
            e.preventDefault();
            redo();
         }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
   }, [undo, redo]);

   return {
      setDocWithHistory,
      undo, redo,
      canUndo: past.current.length > 0,
      canRedo: future.current.length > 0,
      // expose version so consumers can opt-in re-render
      _version: version,
   };
}
